---
title: "Getting data out of Chosen"
description: "The ways to export data from Chosen — CSV and resume-ZIP exports from the candidates table, what analytics can export, the Notion mirror, and the API."
category: "reference"
keywords:
  - "export Chosen data"
  - "candidate data export"
  - "ATS data export"
  - "export recruiting data"
schema: "HowTo"
nav:
  section: "reference"
  order: 70
---

Your data in Chosen is yours, and there's more than one way to get it out — a file download, a live sync, or a programmatic pull. This page is which method fits which need.

## CSV and resume exports from the candidates table

The main export lives at the top of the [candidates table](/guides/candidates/table-and-boards). Two formats:

- **CSV** — every parsed field, plus status and owner, one row per candidate. This is the format for a spreadsheet, a report, or moving data into another system.
- **Resumes (ZIP)** — the original resume files for the candidates in view, bundled into one archive.

Both respect the **current filter**. The filter bar is how you scope an export: filter the table to "engineering candidates who reached Interview" and the export contains exactly those people and no one else. Set the filter before you export.

<Callout type="info" title="Exports build in the background">
A CSV or resume ZIP isn't generated the instant you click. Chosen assembles the file in the background and the download is ready a moment later — fast for a short list, longer for a large one. You don't have to keep the page open and wait. Full detail in [bulk import and export](/guides/candidates/bulk-import-and-export).
</Callout>

## What analytics can and can't export

The [analytics](/guides/analytics) section shows pipeline metrics, source breakdowns, and team performance as charts. Individual charts export two ways — as an image to drop into a deck, or as a CSV of the numbers behind that chart.

What analytics does **not** do is export the underlying per-candidate rows behind a chart. If you need the raw data — the actual list of candidates a metric is counted from — go back to the candidates table, apply a filter that matches the metric, and export that as CSV. Analytics is for the picture; the table is for the rows.

## The Notion sync — a live mirror

If you connect [Notion](/getting-started/integrations/notion), Chosen keeps a synced database of your candidates and jobs there. This isn't a one-time export — it's a **live mirror** that stays current as your data changes in Chosen.

Use it when you want your data continuously visible somewhere your team already works, rather than a file frozen at the moment you downloaded it. It's the right tool for an always-on view; a CSV is the right tool for a snapshot.

## The API — for structured, repeatable pulls

For a programmatic pull — feeding a data warehouse, a custom dashboard, a scheduled report — use the API. It returns structured data you can request on a schedule, with no manual download step.

API access needs an [API key](/reference/api-keys), scoped to a role. A read-only export job should carry a read-only key. The public developer API, with the full endpoint reference, is documented at [developers.chosenhq.com](https://developers.chosenhq.com).

## Which method to use

| You want | Use |
| --- | --- |
| A spreadsheet of candidates, filtered | CSV export from the table |
| The original resume files | Resume ZIP export from the table |
| A chart for a report or deck | Image export from analytics |
| A live, always-current copy elsewhere | The [Notion](/getting-started/integrations/notion) sync |
| A scheduled or programmatic pull | The API with an [API key](/reference/api-keys) |

One rule covers most of it: for a **snapshot**, export a file; for something that should **stay current**, use the Notion sync or the API.
